import { Component } from 'react'
import * as Sentry from '@sentry/react'

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidMount() {
    const { isProdOrStaging } = this.props
    window.onerror = (message, source, lineno, colno, error) => {
      if (!isProdOrStaging) {
        console.error('Global Error Captured:', {
          message,
          source,
          lineno,
          colno,
          error
        })
      }
    }

    window.addEventListener('unhandledrejection', (event) => {
      let error = event.reason

      if (!(error instanceof Error)) {
        error = new Error(
          `Unhandled rejection: ${JSON.stringify(event.reason)}`
        )
      }

      if (isProdOrStaging && Sentry) {
        Sentry.captureException(error, {
          extra: {
            event,
            reason: event.reason,
            promise: event.promise
          }
        })
      }

      console.error(
        'An unexpected error occurred, please try again or contact support.',
        isProdOrStaging ? null : event.reason
      )
    })

    window.addEventListener('error', (event) => {
      let error = event.error

      if (!(error instanceof Error)) {
        error = new Error(
          event.message ||
            `Unhandled error at ${event.filename}:${event.lineno}:${event.colno}`
        )
      }

      if (isProdOrStaging && Sentry) {
        Sentry.captureException(error, {
          extra: {
            event,
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
          }
        })
      }

      console.error(
        'An unexpected error occurred, please try again or contact support.',
        isProdOrStaging ? null : event.error
      )
    })
  }

  componentDidCatch(error, errorInfo) {
    const { setError, setInfo, isProdOrStaging } = this.props
    if (isProdOrStaging && Sentry) {
      Sentry.captureException(error)
    }

    console.error(
      'An unexpected error occurred, please try again or contact support:',
      isProdOrStaging ? null : error,
      isProdOrStaging ? null : errorInfo
    )

    setError(error)
    setInfo(errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return null
    }

    return this.props.children
  }
}

export default ErrorBoundary
